import { useForm } from '@nerdgeschoss/react-use-form-library';
import React, { useEffect } from 'react';
import { FormForm } from '../../../components/Form/FormForm';
import { FormTextField } from '../../../components/Form/TextField/FormTextField';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { graphql } from '../../../generated';
import { t } from '../../../locales/translate';
import { useLazyQueryHandler, useMutationHandler } from '../../../model/graphql';
import { handleError } from '../../../model/notification';

const PRICING_CATEGORY_TO_EDIT = graphql(`
  query PricingCategoryToEdit($venueLayoutId: ID!, $id: ID!) {
    venueLayout(id: $venueLayoutId) {
      pricingCategory(id: $id) {
        id
        name
      }
    }
  }
`);

const CREATE_PRICING_CATEGORY = graphql(`
  mutation CreatePricingCategory($venueLayoutId: ID!, $name: String!) {
    createPricingCategory(input: { venueLayoutId: $venueLayoutId, name: $name }) {
      pricingCategory {
        id
        name
        venueLayout {
          id
          pricingCategories {
            id
          }
        }
      }
    }
  }
`);

const UPDATE_PRICING_CATEGORY = graphql(`
  mutation UpdatePricingCategory($id: ID!, $name: String) {
    updatePricingCategory(input: { id: $id, name: $name }) {
      pricingCategory {
        id
        name
      }
    }
  }
`);

interface Props {
  venueLayoutId: string;
  pricingCategoryId?: string;
  onClose: () => void;
  onCreate?: () => void;
  onEdit?: () => void;
}

export function PricingCategoryForm({
  venueLayoutId,
  pricingCategoryId,
  onClose,
  onCreate,
  onEdit,
}: Props): JSX.Element {
  const [loadPricingCategory, { data, loading, called }] = useLazyQueryHandler(PRICING_CATEGORY_TO_EDIT);
  if (!called && pricingCategoryId) {
    loadPricingCategory({ variables: { venueLayoutId, id: pricingCategoryId } });
  }
  const pricingCategory = data?.venueLayout?.pricingCategory;
  const [createPricingCategory] = useMutationHandler(CREATE_PRICING_CATEGORY);
  const [updatePricingCategory] = useMutationHandler(UPDATE_PRICING_CATEGORY);

  const { changes, fields, dirty, valid, onSubmit, updateFields } = useForm({
    model: {
      name: pricingCategory?.name,
    },
    onSubmit: async () => {
      const gqlChanges = { name: changes.name ?? '' };
      if (pricingCategoryId) {
        await updatePricingCategory({ variables: { ...gqlChanges, id: pricingCategoryId } });
        onEdit?.();
      } else {
        await createPricingCategory({ variables: { ...gqlChanges, venueLayoutId: venueLayoutId } });
        onCreate?.();
      }
      onClose();
    },
    validations: {
      name: 'required',
    },
    onSubmitError: handleError,
  });
  useEffect(() => {
    updateFields({ ...pricingCategory });
  }, [pricingCategory?.id]);

  if (loading) return <LoadingPage />;

  return (
    <FormForm onSubmit={onSubmit} onClose={onClose} canSubmit={dirty && valid}>
      <FormTextField {...fields.name} label={t.model.pricingCategory.name} />
    </FormForm>
  );
}
